import dayjs from 'dayjs';

import { DateRange } from '@cast/shared-types';

import { TimeSeries, TimeSeriesEntry, Timestamp } from 'types/metrics';

/**
 * Required to overcome a long standing bug with dayjs ".utcOffset"
 * where it returns incorrect offset during DST transition.
 */
const getTimeZoneOffset = (date: Date, timeZone: string): number => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
  });

  const parts = formatter.formatToParts(date);
  const offsetPart = parts.find((part) => part.type === 'timeZoneName');

  if (!offsetPart?.value) return 0;

  // Parse "GMT+2" or "GMT-5:30" format
  const match = offsetPart.value.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return 0;

  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = match[3] ? parseInt(match[3], 10) : 0;

  return sign * (hours * 60 + minutes);
};

const compensateTimestampForDST = (
  utcTimestamp: Timestamp,
  referenceOffset: number,
  timezone: string
): Timestamp => {
  const date = dayjs.utc(utcTimestamp);
  const offset = getTimeZoneOffset(date.toDate(), timezone);

  if (offset === referenceOffset) {
    return utcTimestamp;
  }

  const newDate = dayjs(
    date.add(referenceOffset, 'minutes').utcOffset(offset, true).format()
  );
  return newDate.toISOString();
};

/**
 * Compensates timeseries data for DST transitions by adjusting timestamps
 * before DST shifts to maintain uniform spacing when viewed in browser timezone.
 *
 * The trailing (most recent) datapoint is the source of truth - all earlier
 * points are adjusted relative to it to counteract DST offset changes.
 *
 * If `fillGaps` is used, this function must be called **after**, i.e. with the
 * result of `fillGaps`, as otherwise gap filling logic will mess with the DST gap.
 */
export function compensateDSTv2<T extends TimeSeriesEntry<object>>(
  data: T[],
  timezone: string
): T[] {
  if (data.length === 0) {
    return data;
  }

  // Use the trailing (most recent) point as source of truth.
  // Get UTC offset in minutes for the last point
  const lastDate = dayjs.utc(data[data.length - 1].timestamp);
  const lastOffsetMinutes = getTimeZoneOffset(lastDate.toDate(), timezone);

  const firstDate = dayjs.utc(data[0].timestamp);
  const firstOffsetMinutes = getTimeZoneOffset(firstDate.toDate(), timezone);

  if (lastOffsetMinutes === firstOffsetMinutes) {
    return data;
  }

  const mapped = data.map((point) => {
    const newTimestamp = compensateTimestampForDST(
      point.timestamp,
      lastOffsetMinutes,
      timezone
    );
    if (newTimestamp === point.timestamp) {
      return point;
    }
    return {
      ...point,
      timestamp: newTimestamp,
    };
  });

  // After spring-forward compensation, pre-DST points shift onto the same
  // timestamp as the first post-DST point. Remove duplicates, keeping the last
  // occurrence (the authentic post-transition entry).
  const seenTimestamps = new Set<string>();
  const deduped: T[] = [];

  for (let i = mapped.length - 1; i >= 0; i--) {
    const item = mapped[i];

    if (!seenTimestamps.has(item.timestamp)) {
      seenTimestamps.add(item.timestamp);
      deduped.push(item);
    }
  }

  // Reverse to the original ascending order
  return deduped.reverse();
}

/**
 * Account for DST shifts, adjust timestamps to end range timezone
 * @deprecated use `compensateDSTv2` that shifts only the relevant portion of the data.
 */
export const compensateDST = <T extends TimeSeries<any>>(
  range: DateRange,
  data: T
): T => {
  const [from, to] = range;
  const fromOffset = from.utcOffset();
  const toOffset = to.utcOffset();
  const nowOffset = dayjs().utcOffset();

  // start and end ranges are in same timezone, doesn't need adjustment or its month of DST, but DST is not in effect yet
  if (fromOffset === toOffset || fromOffset === nowOffset) {
    return data;
  }

  const offsetDiff = fromOffset - toOffset;

  return data.map((item) => {
    // add offset difference to timestamp
    const timestamp =
      typeof item.timestamp === 'string'
        ? dayjs(item.timestamp).add(offsetDiff, 'minute').toISOString()
        : dayjs.unix(item.timestamp).add(offsetDiff, 'minute').unix();

    return {
      ...item,
      timestamp,
    };
  }) as T;
};
